import moment from "moment/moment";

import {
  CUTOFF_TIMES_CACHE_KEY,
  CutoffTimes,
} from "@globals/hooks/useCutoffTimes";
import {DateString} from "@globals/types/utilityTypes";
import addBusinessDays from "@globals/utils/addBusinessDays";
import toDateString, {checkValidDateStr} from "@globals/utils/toDateString";

const DEFAULT_NUMBER_OF_MENU_DATES = 5;
const DEFAULT_CUTOFF_DAYS = 1;
const DEFAULT_ORDER_CUTOFF = "10:00AM";

export const getPredictedCutoffTimes = (): {
  cutoffDays: number;
  orderCutoff: string;
} => {
  const cachedCutoffTimes = localStorage.getItem(CUTOFF_TIMES_CACHE_KEY);
  const {cutoff_days: cutoffDays, order_cutoff: orderCutoff} = cachedCutoffTimes
    ? (JSON.parse(cachedCutoffTimes) as CutoffTimes)
    : {cutoff_days: DEFAULT_CUTOFF_DAYS, order_cutoff: DEFAULT_ORDER_CUTOFF};
  return {cutoffDays, orderCutoff};
};

export const isPredictedAfterCutoff = (): boolean => {
  // This might be inaccurate if the user's device's time zone differs from that
  // of the location, but we don't want to over-optimize for this case now!
  const {orderCutoff} = getPredictedCutoffTimes();
  const orderCutoffToday = moment(orderCutoff, "hh:mmA");
  return moment().isAfter(orderCutoffToday);
};

export const getSoonestBusinessDay = (): DateString => {
  // Add 1 business day to yesterday to get the soonest business day
  const soonestDate = new Date();
  soonestDate.setDate(soonestDate.getDate() - 1);
  addBusinessDays(soonestDate, 1);
  return toDateString(soonestDate);
};

export const getPredictedLatestDate = (): DateString => {
  // Follow the pattern of getPredictedDate(), but assuming most extreme values
  const predictedDate = new Date();

  // Assume the standard number of menu dates for a non-weekend hub
  addBusinessDays(predictedDate, DEFAULT_NUMBER_OF_MENU_DATES);

  // Add a business day for the max days_before_cutoff value of 1
  addBusinessDays(predictedDate, 1);

  // Add a business day to assume it's after order cutoff
  addBusinessDays(predictedDate, 1);

  return toDateString(predictedDate);
};

export const getPredictedDate = (menuDayIndex: number): DateString => {
  // Starting today
  const predictedDate = new Date();

  // Add N-1 business days to get the Nth menu day
  addBusinessDays(predictedDate, menuDayIndex);

  // Add additional business days based on cached/default "cutoff days ahead"
  const {cutoffDays} = getPredictedCutoffTimes();
  addBusinessDays(predictedDate, cutoffDays);

  // Add 1 more business day if we predict it's after order cutoff
  if (isPredictedAfterCutoff()) {
    addBusinessDays(predictedDate, 1);
  }

  return toDateString(predictedDate);
};

export const isTargetDatePlausibleValidMenuDate = (
  validTargetDate: DateString
): boolean => {
  const dateMoment = moment(validTargetDate);
  if (dateMoment.isBefore(getSoonestBusinessDay())) {
    return false;
  }
  return dateMoment.isSameOrBefore(getPredictedLatestDate());
};

export const getPredictedDateFromDateParam = (
  targetDate: string
): DateString | undefined => {
  if (!checkValidDateStr(targetDate)) {
    // Date param is an invalid format: we'll show an error message
    return undefined;
  }
  if (isTargetDatePlausibleValidMenuDate(targetDate)) {
    // This date could plausibly be a current menu date, so we'll use it
    return targetDate;
  }
  // Seems like this is too early/late to be a valid menu date; we'll use day 1
  return getPredictedDate(0);
};

export function predictMenuDate(
  dateParam: string | null,
  menuDay: string | undefined
): DateString | undefined {
  if (dateParam) {
    // Use newer ?date=yyyy-mm-dd query param
    return getPredictedDateFromDateParam(dateParam);
  }
  // Set predicted date based on Nth menu day, e.g. from old /n+1/ URL param;
  // predicted, since menu dates are still loading
  return getPredictedDate(Number(menuDay ?? 1) - 1);
}
